import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'

import { fmtMoney, fmtDateShort as fmtDate, toISO } from '../../utils/formatters'
import { ARROW_L, ARROW_R, CHECK as CHECK_MARK, CLOCK } from '../../utils/icons'
import CustomersTab from './CustomersTab'
import QuickQuestionButton from './QuickQuestionButton'
import { buildMailto, buildSmsUrl, logFollowUpAttempt, markContacted } from '../../lib/followUpLinks'

var JOB_TYPES = [
  { value: 'above_ground', label: 'Above Ground Pool' },
  { value: 'inground_residential', label: 'Inground - Residential' },
  { value: 'inground_commercial', label: 'Inground - Commercial' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'repair', label: 'Repair' },
]

export default function StorePortalPage() {
  var { user, profile } = useAuth()
  var [tab, setTab] = useState('customers')
  var [myJobs, setMyJobs] = useState([])
  var [pricing, setPricing] = useState([])
  var [settings, setSettings] = useState(null)
  var [jobCounts, setJobCounts] = useState({})
  var [loading, setLoading] = useState(true)
  // Only gate the full-page "Loading..." screen on the very first mount.
  // Subsequent fetchData runs (e.g. after month navigation or submit) should
  // keep the UI visible to avoid unmounting + remounting child tabs like
  // CustomersTab, which caused a visible flash.
  var [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  var [currentMonth, setCurrentMonth] = useState(new Date())
  var [selectedDate, setSelectedDate] = useState(null)
  var [showRequest, setShowRequest] = useState(false)
  var [requestForm, setRequestForm] = useState({})
  var [saving, setSaving] = useState(false)
  var [salespeople, setSalespeople] = useState([])
  var [newSP, setNewSP] = useState({ full_name: '', phone: '', email: '' })
  var [message, setMessage] = useState('')

  // Calculator state
  var [poolType, setPoolType] = useState('round_install')
  var [selectedSize, setSelectedSize] = useState(null)
  var [selectedExtras, setSelectedExtras] = useState([])
  var [customLines, setCustomLines] = useState([])
  var [showPassword, setShowPassword] = useState(false)
  var [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' })
  var [pwSaving, setPwSaving] = useState(false)
  var [pwMessage, setPwMessage] = useState('')

  // Follow-up compose modal (Requests tab)
  var [followUp, setFollowUp] = useState(null) // { request, subject, body }
  var [followUpOpening, setFollowUpOpening] = useState(false)

  // Requests tab filter state
  var [reqFilter, setReqFilter] = useState('all') // all | new | pending | scheduled | needs_followup

  // Nav from a request card into the customer detail on the Customers tab.
  // Stored as an object so clicking the same customer twice still triggers the
  // effect in CustomersTab (new object identity).
  var [customerNav, setCustomerNav] = useState(null) // { id, bump }

  // Store customers cached for the "Who is this for?" picker in the new-
  // request modal. Fetched lazily on modal open (and after a submit).
  var [storeCustomers, setStoreCustomers] = useState([])
  var [customerPickerSearch, setCustomerPickerSearch] = useState('')
  var [reqCustomerId, setReqCustomerId] = useState(null) // linked customer for pending request
  var [reqIsNewCustomer, setReqIsNewCustomer] = useState(false)

  var fetchData = useCallback(async function() {
    // eslint-disable-next-line no-console
    console.log('[StorePortalPage] fetchData fired')
    setLoading(true)
    var { data: myRequests } = await supabase.from('store_requests').select('*')
      .eq('store_user_id', user.id)
      .order('created_at', { ascending: false })
    setMyJobs(myRequests || [])

    var { data: spData } = await supabase.from('store_salespeople').select('*').eq('store_partner_id', user.id).eq('is_active', true).order('full_name')
    setSalespeople(spData || [])
    var { data: pData } = await supabase.from('pricing').select('*')
    setPricing(pData || [])

    var { data: sData } = await supabase.from('schedule_settings').select('*').eq('business_id', 'pools').single()
    setSettings(sData || { max_installs_per_day: 3, blocked_dates: [] })

    var monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1)
    var firstDay = new Date(monthStart)
    firstDay.setDate(firstDay.getDate() - firstDay.getDay())
    var lastDay = new Date(firstDay)
    lastDay.setDate(lastDay.getDate() + 41)

    var { data: allJobs } = await supabase.from('jobs').select('start_date')
      .eq('business_id', 'pools')
      .neq('status', 'cancelled')
      .gte('start_date', toISO(firstDay))
      .lte('start_date', toISO(lastDay))

    var counts = {}
    if (allJobs) { allJobs.forEach(function(j) { counts[j.start_date] = (counts[j.start_date] || 0) + 1 }) }
    setJobCounts(counts)
    setLoading(false)
    setHasLoadedOnce(true)
  }, [user.id, currentMonth])

  useEffect(function() { fetchData() }, [fetchData])

  // Lazy-fetch store customers for the "Who is this for?" picker in the
  // new-request modal. Cached on first load; refetched after a submit that
  // creates a new customer so the dropdown stays fresh.
  var fetchStoreCustomers = useCallback(async function() {
    var { data, error } = await supabase.from('store_customers').select('*')
      .eq('store_user_id', user.id).order('client_name')
    if (error) { console.error('[StorePortalPage] fetch store customers failed', error); return }
    setStoreCustomers(data || [])
  }, [user.id])

  // Fetch customers once on mount so both the request-modal picker and the
  // floating Quick Question button's autocomplete have data ready.
  useEffect(function() {
    fetchStoreCustomers()
  }, [fetchStoreCustomers])

  // Reset the picker state each time the modal opens fresh.
  useEffect(function() {
    if (showRequest) {
      setCustomerPickerSearch('')
      // Don't reset reqCustomerId here — we want it to persist if calculator
      // already bound one. It's cleared when modal closes below.
    } else {
      setReqCustomerId(null)
      setReqIsNewCustomer(false)
      setCustomerPickerSearch('')
    }
  }, [showRequest])

  // Pick an existing customer from the picker. Fills the form fields from
  // the customer record; the user can still edit anything before submit.
  var pickCustomer = function(c) {
    setReqCustomerId(c.id)
    setReqIsNewCustomer(false)
    setRequestForm(function(f) {
      return Object.assign({}, f, {
        client_name: c.client_name || '',
        client_phone: c.client_phone || f.client_phone || '',
        client_email: c.client_email || f.client_email || '',
        site_address: c.site_address || f.site_address || '',
        // Pre-fill pool info from the customer if the form doesn't already
        // have calculator-set values (job_type/pool_size come from the
        // calculator and should win).
        job_type: f.job_type || c.pool_type || 'above_ground',
        pool_size: f.pool_size || c.pool_size || '',
      })
    })
  }
  var clearPickedCustomer = function() {
    setReqCustomerId(null)
    setReqIsNewCustomer(false)
  }

  // --- Requests tab: open-only list + filter chips ---
  var openRequests = (myJobs || []).filter(function(r) {
    return r.status === 'pending' || r.status === 'accepted'
  })
  var THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000
  var SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000
  var needsFollowUp = function(r) {
    if (r.status !== 'pending') return false
    var age = Date.now() - new Date(r.created_at).getTime()
    if (age < THREE_DAYS_MS) return false
    if (!r.last_contact_at) return true
    return Date.now() - new Date(r.last_contact_at).getTime() > SEVEN_DAYS_MS
  }
  var filteredRequests = openRequests.filter(function(r) {
    if (reqFilter === 'all') return true
    if (reqFilter === 'new') return r.status === 'pending' && !r.last_contact_at
    if (reqFilter === 'pending') return r.status === 'pending'
    if (reqFilter === 'scheduled') return r.status === 'accepted'
    if (reqFilter === 'needs_followup') return needsFollowUp(r)
    return true
  })
  var reqCounts = {
    all: openRequests.length,
    new: openRequests.filter(function(r) { return r.status === 'pending' && !r.last_contact_at }).length,
    pending: openRequests.filter(function(r) { return r.status === 'pending' }).length,
    scheduled: openRequests.filter(function(r) { return r.status === 'accepted' }).length,
    needs_followup: openRequests.filter(needsFollowUp).length,
  }

  // Calendar helpers
  var getDaysInMonth = function() {
    var year = currentMonth.getFullYear()
    var month = currentMonth.getMonth()
    var firstDay = new Date(year, month, 1)
    var startPad = firstDay.getDay()
    var days = []
    for (var i = 0; i < startPad; i++) { var d = new Date(year, month, -startPad + i + 1); days.push({ date: d, iso: toISO(d), inMonth: false }) }
    var daysInMonth = new Date(year, month + 1, 0).getDate()
    for (var j = 1; j <= daysInMonth; j++) { var dd = new Date(year, month, j); days.push({ date: dd, iso: toISO(dd), inMonth: true }) }
    while (days.length < 42) { var extra = new Date(year, month + 1, days.length - startPad - daysInMonth + 1); days.push({ date: extra, iso: toISO(extra), inMonth: false }) }
    return days
  }

  var getMaxForDate = function(dateStr) {
    var overrides = settings?.monthly_overrides || {}
    var monthKey = dateStr ? dateStr.substring(0, 7) : null
    if (monthKey && overrides[monthKey] !== undefined) return overrides[monthKey]
    return settings?.max_installs_per_day || 3
  }
  // eslint-disable-next-line no-unused-vars
  var maxPerDay = settings?.max_installs_per_day || 3
  var blockedDates = settings?.blocked_dates || []

  var getDateStatus = function(iso) {
    var today = toISO(new Date())
    if (iso < today) return 'past'
    if (new Date(iso + 'T12:00:00').getDay() === 0) return 'blocked'
    if (blockedDates.includes(iso)) return 'blocked'
    var count = jobCounts[iso] || 0
    var dayMax = getMaxForDate(iso)
    if (count >= dayMax) return 'full'
    if (count >= dayMax - 1) return 'limited'
    return 'open'
  }

  var statusLabel = { past: '', blocked: 'BLOCKED', full: 'FULL', limited: 'LIMITED', open: 'OPEN' }
  var statusColor = { past: 'transparent', blocked: '#e05c5c', full: '#e05c5c', limited: '#b48c3c', open: '#4a8c6a' }

  var prevMonth = function() { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)) }
  var nextMonth = function() { setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)) }

  // Calculator helpers
  var sizes = pricing.filter(function(p) { return p.category === poolType })
  var extras = pricing.filter(function(p) { return p.category === 'extra' })
  var toggleExtra = function(id) { setSelectedExtras(function(prev) { return prev.includes(id) ? prev.filter(function(x) { return x !== id }) : [...prev, id] }) }
  var addCustom = function() { setCustomLines([...customLines, { description: '', price: 'TBD' }]) }
  var updateCustom = function(i, field, val) { var n = [...customLines]; n[i][field] = val; setCustomLines(n) }
  var removeCustom = function(i) { setCustomLines(customLines.filter(function(_, idx) { return idx !== i })) }

  var poolPrice = selectedSize ? parseFloat(pricing.find(function(p) { return p.id === selectedSize })?.price) || 0 : 0
  var extrasTotal = selectedExtras.reduce(function(sum, id) { return sum + (parseFloat(pricing.find(function(p) { return p.id === id })?.price) || 0) }, 0)
  var hasTBD = customLines.some(function(l) { return l.price === 'TBD' || l.price === 'tbd' }) || requestForm.half_sunk
  var customTotal = customLines.reduce(function(sum, l) { return sum + (l.price === 'TBD' || l.price === 'tbd' ? 0 : (parseFloat(l.price) || 0)) }, 0)
  var tearDownTotal = requestForm.tear_down ? 500 : 0
  var grandTotal = (requestForm.half_sunk ? 0 : poolPrice) + extrasTotal + customTotal + tearDownTotal

  var selectedSizeData = selectedSize ? pricing.find(function(p) { return p.id === selectedSize }) : null

  // Build description from calculator selections
  var buildDescription = function() {
    var parts = []
    if (selectedSizeData) parts.push(poolType.replace(/_/g, ' ').toUpperCase() + ' - ' + selectedSizeData.size_label)
    selectedExtras.forEach(function(id) { var ex = pricing.find(function(p) { return p.id === id }); if (ex) parts.push('Extra: ' + ex.size_label) })
    if (requestForm.tear_down) parts.push('ADD-ON: Pool Tear Down & Place by Road - $500')
    if (requestForm.half_sunk) parts.push('ADD-ON: Half Sunk Aboveground (Install price TBD - call to verify pricing)')
    customLines.filter(function(l) { return l.description }).forEach(function(l) { parts.push(l.description + (l.price === 'TBD' || l.price === 'tbd' ? ' (TBD)' : '')) })
    if (hasTBD) parts.push('\n** CONTAINS TBD ITEMS - PENDING OWNER REVIEW **')
    return parts.join('\n')
  }

  // Schedule from calculator
  var scheduleFromCalculator = function() {
    if (!selectedSize) { setMessage('Select a pool size first.'); setTimeout(function() { setMessage('') }, 3000); return }
    var shape = poolType.includes('round') ? 'round' : 'oval'
    var sizeLabel = selectedSizeData?.size_label || ''
    setRequestForm({
      client_name: '', client_phone: '', client_email: '',
      site_address: '', job_type: poolType.includes('liner') ? 'renovation' : 'above_ground',
      contract_value: grandTotal.toFixed(2),
      notes: buildDescription(),
      pool_size: sizeLabel, pool_shape: shape
    })
    setTab('schedule')
    setMessage('Select a date to schedule this ' + fmtMoney(grandTotal) + ' install.')
    setTimeout(function() { setMessage('') }, 5000)
  }

  var selectDate = function(day) {
    var status = getDateStatus(day.iso)
    if (status === 'past' || status === 'blocked' || status === 'full') return
    setSelectedDate(day.iso)
    setShowRequest(true)
    if (!requestForm.contract_value) {
      setRequestForm({
        client_name: '', client_phone: '', client_email: '',
        site_address: '', job_type: 'above_ground',
        contract_value: '', notes: '',
        pool_size: '', pool_shape: 'round'
      })
    }
  }

  var submitRequest = async function() {
    if (!requestForm.client_name || !requestForm.client_name.trim()) {
      setMessage('Customer name is required.')
      return
    }
    setSaving(true)
    var storeName = profile?.store_name || profile?.full_name || 'Store Partner'

    // Build line items from selections
    var lineItems = []
    if (selectedSizeData) lineItems.push({ description: poolType.replace(/_/g, ' ').toUpperCase() + ' - ' + selectedSizeData.size_label, price: requestForm.half_sunk ? 'TBD' : poolPrice })
    selectedExtras.forEach(function(id) { var ex = pricing.find(function(p) { return p.id === id }); if (ex) lineItems.push({ description: ex.size_label, price: parseFloat(ex.price) || 0 }) })
    var extrasJson = []
    if (requestForm.tear_down) extrasJson.push({ description: 'Pool Tear Down & Place by Road', price: 500 })

    var reqPoolType = requestForm.job_type || null
    var reqPoolSize = (requestForm.pool_shape || requestForm.pool_size) ? ((requestForm.pool_shape || '') + ' ' + (requestForm.pool_size || '')).trim() || null : null

    // DB call 1: Ensure a store_customers row exists and grab its id so we
    // can link store_requests.customer_id. If the user picked an existing
    // customer, skip the upsert and use that id directly.
    var customerId = reqCustomerId
    if (!customerId) {
      var upsertRes = await supabase.from('store_customers').upsert({
        store_user_id: user.id,
        store_name: storeName,
        client_name: requestForm.client_name.trim(),
        client_phone: requestForm.client_phone || null,
        client_email: requestForm.client_email || null,
        site_address: requestForm.site_address || null,
        pool_type: reqPoolType,
        pool_size: reqPoolSize,
        customer_type: 'customer',
        status: 'active',
        last_contact_date: new Date().toISOString().split('T')[0],
      }, { onConflict: 'store_user_id,client_name' }).select().single()
      if (upsertRes.error || !upsertRes.data) {
        console.error('[StorePortalPage] customer upsert failed', upsertRes.error)
        setMessage('Error saving customer: ' + ((upsertRes.error && upsertRes.error.message) || 'unknown'))
        setSaving(false); return
      }
      customerId = upsertRes.data.id
    }

    // DB call 2: Insert the store request, linked to the customer.
    var { data: newReq, error } = await supabase.from('store_requests').insert({
      store_user_id: user.id,
      store_name: storeName,
      business_id: 'pools',
      customer_id: customerId,
      client_name: requestForm.client_name.trim(),
      client_email: requestForm.client_email || null,
      client_phone: requestForm.client_phone || null,
      site_address: requestForm.site_address || null,
      preferred_date: selectedDate || requestForm.preferred_date || null,
      pool_type: reqPoolType,
      pool_size: reqPoolSize,
      notes: requestForm.notes || null,
      line_items: lineItems.length > 0 ? lineItems : null,
      extras: extrasJson.length > 0 ? extrasJson : null,
      custom_items: customLines.filter(function(l) { return l.description }).length > 0 ? customLines.filter(function(l) { return l.description }) : null,
      half_sunk: !!requestForm.half_sunk,
      tear_down: !!requestForm.tear_down,
      status: 'pending',
    }).select().single()
    if (error) { setMessage('Error: ' + error.message); setSaving(false); return }

    // DB call 3: If the picked customer is missing pool info, patch it with
    // what was captured on this request. Non-blocking.
    if (reqCustomerId) {
      var existing = storeCustomers.find(function(c) { return c.id === reqCustomerId })
      var patch = {}
      if (existing && !existing.pool_type && reqPoolType) patch.pool_type = reqPoolType
      if (existing && !existing.pool_size && reqPoolSize) patch.pool_size = reqPoolSize
      if (Object.keys(patch).length > 0) {
        supabase.from('store_customers').update(patch).eq('id', reqCustomerId)
          .then(function(r) { if (r.error) console.warn('Customer patch failed', r.error) })
      }
    }

    // Show success immediately — don't wait for notification
    setShowRequest(false)
    setRequestForm({})
    setSelectedSize(null)
    setSelectedExtras([])
    setCustomLines([])
    setReqCustomerId(null)
    setReqIsNewCustomer(false)
    setSaving(false)
    setMessage('Request submitted! The installer team will review it shortly.')
    setTimeout(function() { setMessage('') }, 5000)

    // Notify owner (non-blocking).
    if (newReq) {
      supabase.from('notifications').insert({
        user_id: 'b85cd872-6750-494a-960e-7b8d32498154',
        type: 'store_request',
        title: 'New Store Submission',
        message: storeName + ' submitted a new request for ' + (requestForm.client_name || 'a customer') + '. Review and schedule when ready.',
        link: '/store-requests?request=' + newReq.id,
        related_id: newReq.id,
        business_id: 'pools'
      }).then(function(r) { if (r.error) console.warn('Notify owner failed', r.error) })
    }

    // Refresh parent data + picker list.
    fetchData()
    fetchStoreCustomers()
  }

  // --- Follow-up: compose modal for a request, hands off to the user's email app ---
  var openFollowUpEmail = function(req) {
    if (!req.client_email) { alert('No email on file for this request.'); return }
    var firstName = (req.client_name || '').split(' ')[0] || 'there'
    var sizeLabel = req.pool_size ? req.pool_size + ' ' : ''
    var typeLabel = req.pool_type ? (req.pool_type.replace(/_/g, ' ') + ' ') : ''
    var subject = 'Following up on your ' + sizeLabel + typeLabel + 'pool request'
    var body = 'Hi ' + firstName + ',\n\nI wanted to follow up on your request for a ' + sizeLabel + typeLabel +
      'pool. Are you still interested in moving forward? Let me know if you have any questions or would like to schedule.\n\n' +
      'Thanks,\n' + (profile?.full_name || '') + '\n' + (profile?.store_name || '')
    setFollowUp({ request: req, subject: subject, body: body })
  }

  var sendFollowUpEmail = async function() {
    if (!followUp) return
    setFollowUpOpening(true)
    try {
      var req = followUp.request
      await logFollowUpAttempt({
        storeUserId: user.id,
        customerId: req.customer_id || null,
        requestId: req.id,
        clientName: req.client_name,
        clientEmail: req.client_email,
        subject: followUp.subject,
        body: followUp.body,
        sendMethod: 'mailto',
      })
      await markContacted({ customerId: req.customer_id || null, request: req })
      setFollowUp(null)
      window.location.href = buildMailto({
        to: req.client_email,
        subject: followUp.subject,
        body: followUp.body,
      })
    } catch (e) {
      alert('Could not open email app: ' + ((e && e.message) || e))
    }
    setFollowUpOpening(false)
  }

  var openFollowUpSms = async function(req) {
    if (!req.client_phone) { alert('No phone on file for this request.'); return }
    var firstName = (req.client_name || '').split(' ')[0] || 'there'
    var sizeLabel = req.pool_size ? req.pool_size + ' ' : ''
    var typeLabel = req.pool_type ? (req.pool_type.replace(/_/g, ' ') + ' ') : ''
    var partnerName = profile?.full_name || ''
    var storeName = profile?.store_name || 'our team'
    var message = 'Hi ' + firstName + ', this is ' + partnerName + ' from ' + storeName +
      '. Just checking in on your ' + sizeLabel + typeLabel + 'pool request \u2014 are you still interested?'
    try {
      await logFollowUpAttempt({
        storeUserId: user.id,
        customerId: req.customer_id || null,
        requestId: req.id,
        clientName: req.client_name,
        subject: 'SMS follow-up',
        body: message,
        sendMethod: 'sms',
      })
      await markContacted({ customerId: req.customer_id || null, request: req })
      window.location.href = buildSmsUrl(req.client_phone, message)
    } catch (e) {
      alert('Could not open messaging app: ' + ((e && e.message) || e))
    }
  }

  var addSalesperson = async function() { if (!newSP.full_name) return; var { data } = await supabase.from('store_salespeople').insert({ store_partner_id: user.id, full_name: newSP.full_name, phone: newSP.phone, email: newSP.email }).select().single(); if (data) { setSalespeople(function(prev) { return [...prev, data] }); setNewSP({ full_name: '', phone: '', email: '' }) } }
  var removeSalesperson = async function(id) { if (!window.confirm('Remove this salesperson?')) return; await supabase.from('store_salespeople').update({ is_active: false }).eq('id', id); setSalespeople(function(prev) { return prev.filter(function(s) { return s.id !== id }) }) }
  var handleChangePassword = async function() {
    if (profile?.role !== 'store_partner') { setPwMessage('Password changes are only available for store partner accounts.'); return }
    if (pwForm.newPw.length < 8) { setPwMessage('Password must be at least 8 characters.'); return }
    if (pwForm.newPw !== pwForm.confirm) { setPwMessage('Passwords do not match.'); return }
    setPwSaving(true); setPwMessage('')
    var { error } = await supabase.auth.updateUser({ password: pwForm.newPw })
    if (error) { setPwMessage('Error: ' + error.message) }
    else { setPwMessage('Password updated!'); setPwForm({ current: '', newPw: '', confirm: '' }); setTimeout(function() { setShowPassword(false); setPwMessage('') }, 2000) }
    setPwSaving(false)
  }

  var set = function(k, v) { setRequestForm(function(f) { return { ...f, [k]: v } }) }

  // eslint-disable-next-line no-unused-vars
  var requestStatusColors = { pending: '#b48c3c', confirmed: '#4a8c6a', declined: '#e05c5c' }
  var jobStatusColors = { scheduled: '#4a6a8c', active: '#b48c3c', completed: '#4a8c6a', cancelled: '#e05c5c' } // eslint-disable-line no-unused-vars

  if (loading && !hasLoadedOnce) return <div style={s.loading}>Loading...</div>

  return (
    <div style={s.container}>
      <QuickQuestionButton userId={user.id} profile={profile} storeCustomers={storeCustomers} />
      <div style={s.header}>
        <div>
          <div style={s.title}>STORE PARTNER PORTAL</div>
          <div style={s.subtitle}>Welcome, {profile?.store_name || profile?.full_name}</div>
        </div>
      </div>

      {message && <div style={s.message}>{message}</div>}

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:'8px',marginBottom:'20px'}}>
        <div style={s.tabs}>
          {[{id:'customers',label:'MY CUSTOMERS'},{id:'myjobs',label:'MY REQUESTS' + (reqCounts.all ? ' (' + reqCounts.all + ')' : '')},{id:'pricing',label:'BUILD QUOTE'},{id:'schedule',label:'SCHEDULE INSTALL'},{id:'team',label:'MY TEAM'}].map(function(t) {
            return <button key={t.id} onClick={function() { setTab(t.id) }} style={{...s.tab, ...(tab === t.id ? s.tabActive : {})}}>{t.label}</button>
          })}
        </div>
        <button onClick={function() { setShowPassword(true) }} style={{padding:'6px 14px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.4)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:600,letterSpacing:'1px',cursor:'pointer'}}>CHANGE PASSWORD</button>
      </div>

      {/* === PRICING / CALCULATOR TAB === */}
      {tab === 'pricing' && (
        <div>
          <div style={s.sectionTitle}>POOL INSTALL CALCULATOR</div>

          {/* Pool Type Selector */}
          <div style={s.poolTypeRow}>
            {['round_install','round_liner','oval_install','oval_liner'].map(function(pt) {
              return <button key={pt} onClick={function() { setPoolType(pt); setSelectedSize(null) }} style={{...s.poolTypeBtn,...(poolType === pt ? s.poolTypeBtnActive : {})}}>{pt.replace(/_/g, ' ').toUpperCase()}</button>
            })}
          </div>

          {/* Size Grid */}
          <div style={s.sizeGrid}>
            {sizes.map(function(sz) {
              return (
                <button key={sz.id} onClick={function() { setSelectedSize(sz.id) }} style={{...s.sizeBtn,...(selectedSize === sz.id ? s.sizeBtnActive : {})}}>
                  <div style={s.sizeLabel}>{sz.size_label}</div>
                  <div style={s.sizePrice}>{fmtMoney(sz.price)}</div>
                </button>
              )
            })}
          </div>

          {/* Extras & Add-Ons */}
          <div style={{marginTop:'20px'}}>
            <div style={s.sectionTitle}>EXTRAS & ADD-ONS</div>
            <div style={s.extrasGrid}>
              {extras.map(function(ex) {
                return <button key={ex.id} onClick={function() { toggleExtra(ex.id) }} style={{...s.extraBtn,...(selectedExtras.includes(ex.id) ? s.extraBtnActive : {})}}><span>{ex.size_label}</span><span style={s.extraPrice}>{fmtMoney(ex.price)}</span></button>
              })}
              <button onClick={function() { set('tear_down', !requestForm.tear_down) }} style={{...s.extraBtn,...(requestForm.tear_down ? s.extraBtnActive : {})}}><span>Pool Tear Down & Place by Road</span><span style={s.extraPrice}>{fmtMoney(500)}</span></button>
            </div>
          </div>

          {/* Half Sunk option */}
          <div style={{marginTop:'16px'}}>
            <label style={{display:'flex',gap:'10px',alignItems:'flex-start',cursor:'pointer',padding:'10px 14px',backgroundColor:requestForm.half_sunk?'rgba(180,140,60,0.1)':'rgba(255,255,255,0.02)',border:'1px solid '+(requestForm.half_sunk?'rgba(180,140,60,0.3)':'rgba(255,255,255,0.06)'),borderRadius:'4px'}}>
              <input type="checkbox" checked={!!requestForm.half_sunk} onChange={function(e) { set('half_sunk', e.target.checked) }} style={{marginTop:'3px'}} />
              <div><div style={{fontSize:'14px',color:'#ffffff',fontWeight:500}}>Half Sunk Aboveground</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.35)',marginTop:'2px'}}>Installation price becomes TBD</div>{requestForm.half_sunk && <div style={{fontSize:'11px',color:'#b48c3c',marginTop:'4px',fontStyle:'italic'}}>The installers request a phone call to verify pricing and verify options</div>}</div>
            </label>
          </div>

          {/* Custom add-ons */}
          <div style={{marginTop:'20px'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'10px'}}>
              <div style={s.sectionTitle}>CUSTOM ADD-ONS <span style={{fontSize:'9px',color:'rgba(255,255,255,0.25)',marginLeft:'8px'}}>PRICES DEFAULT TO TBD</span></div>
              <button onClick={addCustom} style={s.addLineBtn}>+ CUSTOM</button>
            </div>
            {customLines.map(function(cl, i) {
              return (
                <div key={i} style={{display:'flex',gap:'8px',marginBottom:'6px'}}>
                  <input value={cl.description} onChange={function(e) { updateCustom(i, 'description', e.target.value) }} style={{...s.formInput, flex:2}} placeholder="Description" />
                  <input value={cl.price} onChange={function(e) { updateCustom(i, 'price', e.target.value) }} style={{...s.formInput, flex:1, color: cl.price === 'TBD' || cl.price === 'tbd' ? '#b48c3c' : '#ffffff'}} placeholder="TBD" />
                  <button onClick={function() { removeCustom(i) }} style={s.delSmBtn}>X</button>
                </div>
              )
            })}
          </div>

          {/* Total Bar */}
          <div style={s.totalBar}>
            <div>
              <span style={s.totalBreakdown}>Pool: {requestForm.half_sunk ? 'TBD' : fmtMoney(poolPrice)}</span>
              {extrasTotal > 0 && <span style={s.totalBreakdown}>Extras: {fmtMoney(extrasTotal)}</span>}
              {tearDownTotal > 0 && <span style={s.totalBreakdown}>Tear Down: {fmtMoney(500)}</span>}
              {customTotal > 0 && <span style={s.totalBreakdown}>Custom: {fmtMoney(customTotal)}</span>}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:'20px'}}>
              {hasTBD ? <span style={{...s.grandTotal, color:'#b48c3c'}}>{grandTotal > 0 ? fmtMoney(grandTotal) + ' + TBD' : 'TBD'}</span> : <span style={s.grandTotal}>{fmtMoney(grandTotal)}</span>}
              <button onClick={scheduleFromCalculator} disabled={!selectedSize} style={{...s.scheduleBtn, opacity: selectedSize ? 1 : 0.4}}>
                {ARROW_R} SCHEDULE THIS INSTALL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === SCHEDULE TAB === */}
      {tab === 'schedule' && (
        <div>
          {/* Show pending quote summary if coming from calculator */}
          {requestForm.contract_value && parseFloat(requestForm.contract_value) > 0 && (
            <div style={s.pendingQuote}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',color:'#4a8c6a',marginBottom:'8px'}}>QUOTE READY - SELECT A DATE</div>
              <div style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',whiteSpace:'pre-wrap'}}>{requestForm.notes}</div>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'22px',fontWeight:700,color:'#b48c3c',marginTop:'8px'}}>{fmtMoney(requestForm.contract_value)}</div>
            </div>
          )}

          <div style={s.calHeader}>
            <button onClick={prevMonth} style={s.calNav}>{ARROW_L}</button>
            <div style={s.calMonth}>{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}</div>
            <button onClick={nextMonth} style={s.calNav}>{ARROW_R}</button>
          </div>

          <div style={s.legend}>
            <span style={s.legendItem}><span style={{...s.legendDot, backgroundColor:'#4a8c6a'}} /> Open</span>
            <span style={s.legendItem}><span style={{...s.legendDot, backgroundColor:'#b48c3c'}} /> Limited</span>
            <span style={s.legendItem}><span style={{...s.legendDot, backgroundColor:'#e05c5c'}} /> Full</span>
            <span style={s.legendItem}><span style={{...s.legendDot, backgroundColor:'#555'}} /> Blocked</span>
          </div>

          <div style={s.calGrid}>
            {['SUN','MON','TUE','WED','THU','FRI','SAT'].map(function(d) {
              return <div key={d} style={s.calDayHeader}>{d}</div>
            })}
            {getDaysInMonth().map(function(day, i) {
              var status = getDateStatus(day.iso)
              var isPast = status === 'past'
              var isClickable = status === 'open' || status === 'limited'
              var count = jobCounts[day.iso] || 0
              var remaining = getMaxForDate(day.iso) - count

              return (
                <div key={i}
                  onClick={function() { if (isClickable) selectDate(day) }}
                  style={{
                    ...s.calDay,
                    opacity: !day.inMonth ? 0.3 : isPast ? 0.4 : 1,
                    cursor: isClickable ? 'pointer' : 'default',
                    borderColor: status === 'open' ? 'rgba(74,140,106,0.3)' : status === 'limited' ? 'rgba(180,140,60,0.3)' : status === 'full' || status === 'blocked' ? 'rgba(224,92,92,0.2)' : 'rgba(255,255,255,0.06)',
                    backgroundColor: isClickable ? 'rgba(74,140,106,0.05)' : 'transparent'
                  }}
                >
                  <div style={s.calDayNum}>{day.date.getDate()}</div>
                  {day.inMonth && !isPast && (
                    <div style={{fontSize:'8px', fontWeight:600, color: statusColor[status], letterSpacing:'0.5px', marginTop:'2px'}}>
                      {status === 'open' || status === 'limited' ? remaining + ' SLOT' + (remaining !== 1 ? 'S' : '') : statusLabel[status]}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div style={{fontSize:'11px', color:'rgba(255,255,255,0.25)', marginTop:'12px', textAlign:'center'}}>
            Click an available date to request a pool install.
          </div>
        </div>
      )}

      {/* === MY REQUESTS TAB === */}
      {tab === 'myjobs' && (
        <div>
          <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginBottom:'16px'}}>
            {[
              { id: 'all', label: 'All' },
              { id: 'new', label: 'New' },
              { id: 'pending', label: 'Pending Review' },
              { id: 'scheduled', label: 'Scheduled' },
              { id: 'needs_followup', label: 'Needs Follow-Up' },
            ].map(function(f) {
              var active = reqFilter === f.id
              var count = reqCounts[f.id] || 0
              return (
                <button key={f.id} type="button" onClick={function() { setReqFilter(f.id) }}
                  style={{padding:'6px 12px',backgroundColor: active ? 'rgba(180,140,60,0.15)' : 'transparent',border:'1px solid ' + (active ? 'rgba(180,140,60,0.4)' : 'rgba(255,255,255,0.1)'),borderRadius:'16px',color: active ? '#b48c3c' : 'rgba(255,255,255,0.4)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'}}>
                  {f.label}{count > 0 ? ' (' + count + ')' : ''}
                </button>
              )
            })}
          </div>
          {filteredRequests.length === 0 && (
            <div style={s.empty}>{openRequests.length === 0 ? 'No open requests. Build a quote and submit a request.' : 'No requests match this filter.'}</div>
          )}
          {filteredRequests.map(function(r) {
            var stColor = r.status === 'pending' ? '#b48c3c' : r.status === 'accepted' ? '#4a8c6a' : r.status === 'declined' ? '#e05c5c' : 'rgba(255,255,255,0.3)'
            var canEmail = !!r.client_email
            var canText = !!r.client_phone
            return (
              <div key={r.id} style={{...s.jobCard, borderLeft:'3px solid ' + stColor}}>
                <div style={{flex:1}}>
                  <div style={s.jobTitle}>{r.status === 'pending' ? CLOCK + ' ' : r.status === 'accepted' ? CHECK_MARK + ' ' : ''}{r.client_name}</div>
                  <div style={s.jobMeta}>{r.site_address}{r.preferred_date ? ' ' + String.fromCodePoint(0x2022) + ' ' + fmtDate(r.preferred_date) : ''}</div>
                  <div style={s.jobMeta}>{r.pool_type ? (JOB_TYPES.find(function(t) { return t.value === r.pool_type })?.label || r.pool_type) : ''}{r.pool_size ? ' - ' + r.pool_size : ''}</div>
                  {r.half_sunk && <div style={{fontSize:'11px',color:'#b48c3c',marginTop:'2px'}}>Half Sunk (TBD pricing)</div>}
                  {r.tear_down && <div style={{fontSize:'11px',color:'rgba(255,255,255,0.35)',marginTop:'2px'}}>Tear Down & Place by Road ($500)</div>}
                  <div style={{display:'flex',gap:'6px',marginTop:'8px',flexWrap:'wrap'}}>
                    {r.customer_id && <button type="button" onClick={function() { setCustomerNav({ id: r.customer_id, bump: Date.now() }); setTab('customers') }} style={{padding:'6px 12px',backgroundColor:'rgba(180,140,60,0.15)',border:'1px solid rgba(180,140,60,0.3)',borderRadius:'3px',color:'#b48c3c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1px',cursor:'pointer'}}>VIEW CUSTOMER {String.fromCodePoint(0x2192)}</button>}
                    {canEmail && <button type="button" onClick={function() { openFollowUpEmail(r) }} style={{padding:'6px 12px',backgroundColor:'rgba(74,106,140,0.15)',border:'1px solid rgba(74,106,140,0.3)',borderRadius:'3px',color:'#4a6a8c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1px',cursor:'pointer'}}>SEND FOLLOW-UP EMAIL</button>}
                    {canText && <button type="button" onClick={function() { openFollowUpSms(r) }} style={{padding:'6px 12px',backgroundColor:'rgba(74,140,106,0.15)',border:'1px solid rgba(74,140,106,0.3)',borderRadius:'3px',color:'#4a8c6a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1px',cursor:'pointer'}}>SEND TEXT</button>}
                  </div>
                </div>
                <div style={{textAlign:'right'}}>
                  <div style={{...s.statusBadge, color: stColor}}>{(r.status || 'PENDING').toUpperCase()}</div>
                  {r.decline_reason && <div style={{fontSize:'11px',color:'#e05c5c',marginTop:'4px'}}>{r.decline_reason}</div>}
                  {r.last_contact_at && <div style={{fontSize:'10px',color:'rgba(255,255,255,0.35)',marginTop:'4px'}}>Last contacted {fmtDate(r.last_contact_at)}</div>}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* === FOLLOW-UP COMPOSE MODAL (mailto) === */}
      {followUp && (
        <div style={s.modalOverlay} onClick={function() { if (!followUpOpening) setFollowUp(null) }}>
          <div style={{...s.modal, maxWidth: '520px'}} onClick={function(e) { e.stopPropagation() }}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>SEND FOLLOW-UP EMAIL</div>
              <button type="button" onClick={function() { setFollowUp(null) }} style={s.modalClose}>X</button>
            </div>
            <div style={{fontSize:'12px',color:'rgba(255,255,255,0.45)',marginBottom:'12px'}}>
              To: <span style={{color:'#ffffff'}}>{followUp.request.client_email}</span>
            </div>
            <div><label style={s.formLabel}>SUBJECT</label><input value={followUp.subject} onChange={function(e) { setFollowUp(Object.assign({}, followUp, { subject: e.target.value })) }} style={s.formInput} /></div>
            <div style={{marginTop:'10px'}}><label style={s.formLabel}>MESSAGE</label><textarea value={followUp.body} onChange={function(e) { setFollowUp(Object.assign({}, followUp, { body: e.target.value })) }} style={{...s.formInput, minHeight:'160px', resize:'vertical', lineHeight:1.5}} /></div>
            <div style={{marginTop:'12px',fontSize:'11px',color:'rgba(255,255,255,0.35)',lineHeight:1.5}}>
              This will open your default email app with the message ready to send. The email comes from your own address \u2014 your customer will reply directly to you.
            </div>
            <div style={{display:'flex',gap:'8px',marginTop:'16px'}}>
              <button type="button" disabled={followUpOpening} onClick={function() { setFollowUp(null) }} style={{padding:'10px 20px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.5)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'}}>CANCEL</button>
              <button type="button" disabled={followUpOpening} onClick={sendFollowUpEmail} style={{flex:1,padding:'10px',backgroundColor:'#b48c3c',border:'none',borderRadius:'3px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:700,letterSpacing:'1.5px',cursor:'pointer'}}>{String.fromCodePoint(0x2709, 0xFE0F) + ' OPEN IN EMAIL APP'}</button>
            </div>
          </div>
        </div>
      )}

      {/* === MY CUSTOMERS TAB === */}
      {tab === 'customers' && (
        <CustomersTab userId={user.id} profile={profile} myJobs={myJobs} onRefresh={fetchData} customerNav={customerNav} />
      )}

            {tab === 'team' && (<div><div style={s.sectionTitle}>SALESPEOPLE</div><div style={{fontSize:'12px',color:'rgba(255,255,255,0.3)',marginBottom:'16px'}}>Add your sales team. They will appear as options when scheduling installs.</div>{salespeople.map(function(sp) { return (<div key={sp.id} style={{display:'flex',alignItems:'center',gap:'12px',padding:'12px 16px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',marginBottom:'4px'}}><div style={{flex:1}}><div style={{fontSize:'14px',fontWeight:600,color:'#fff'}}>{sp.full_name}</div><div style={{fontSize:'11px',color:'rgba(255,255,255,0.35)',marginTop:'2px'}}>{sp.phone}{sp.phone && sp.email ? ' | ' : ''}{sp.email}</div></div><button onClick={function() { removeSalesperson(sp.id) }} style={s.delSmBtn}>X</button></div>) })}{salespeople.length === 0 && <div style={s.empty}>No salespeople added yet.</div>}<div style={{marginTop:'16px',padding:'16px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px'}}><div style={{...s.sectionTitle,marginBottom:'12px'}}>ADD SALESPERSON</div><div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'8px',marginBottom:'8px'}}><div><label style={s.formLabel}>NAME *</label><input value={newSP.full_name} onChange={function(e) { setNewSP(function(p) { return {...p, full_name: e.target.value} }) }} style={s.formInput} placeholder="Full Name" /></div><div><label style={s.formLabel}>PHONE</label><input value={newSP.phone} onChange={function(e) { setNewSP(function(p) { return {...p, phone: e.target.value} }) }} style={s.formInput} placeholder="(810) 555-1234" /></div><div><label style={s.formLabel}>EMAIL</label><input value={newSP.email} onChange={function(e) { setNewSP(function(p) { return {...p, email: e.target.value} }) }} style={s.formInput} placeholder="email@example.com" /></div></div><button onClick={addSalesperson} style={s.scheduleBtn}>ADD SALESPERSON</button></div></div>)}

      {/* === PASSWORD MODAL === */}
      {showPassword && <div style={s.modalOverlay} onClick={function(){setShowPassword(false)}}>
        <div style={{...s.modal,maxWidth:'400px'}} onClick={function(e){e.stopPropagation()}}>
          <div style={s.modalHeader}><div style={s.modalTitle}>CHANGE PASSWORD</div><button onClick={function(){setShowPassword(false)}} style={s.modalClose}>X</button></div>
          <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            <div><label style={s.formLabel}>NEW PASSWORD</label><input type="password" value={pwForm.newPw} onChange={function(e){setPwForm(Object.assign({},pwForm,{newPw:e.target.value}))}} style={s.formInput} placeholder="Minimum 8 characters" /></div>
            <div><label style={s.formLabel}>CONFIRM PASSWORD</label><input type="password" value={pwForm.confirm} onChange={function(e){setPwForm(Object.assign({},pwForm,{confirm:e.target.value}))}} style={s.formInput} placeholder="Re-enter new password" /></div>
          </div>
          {pwMessage && <div style={{marginTop:'12px',padding:'10px',borderRadius:'3px',fontSize:'13px',backgroundColor:pwMessage.includes('Error')?'rgba(224,92,92,0.1)':'rgba(74,140,106,0.1)',color:pwMessage.includes('Error')?'#e05c5c':'#4a8c6a',border:'1px solid '+(pwMessage.includes('Error')?'rgba(224,92,92,0.3)':'rgba(74,140,106,0.3)')}}>{pwMessage}</div>}
          <div style={{display:'flex',gap:'8px',marginTop:'16px'}}>
            <button onClick={function(){setShowPassword(false)}} style={{padding:'10px 20px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.5)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'}}>CANCEL</button>
            <button onClick={handleChangePassword} disabled={pwSaving} style={{flex:1,padding:'10px',backgroundColor:'#b48c3c',border:'none',borderRadius:'3px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:700,letterSpacing:'1.5px',cursor:'pointer'}}>{pwSaving?'UPDATING...':'UPDATE PASSWORD'}</button>
          </div>
        </div>
      </div>}

      {/* === REQUEST MODAL === */}
      {showRequest && (
        <div style={s.modalOverlay} onClick={function() { setShowRequest(false) }}>
          <div style={{...s.modal, maxWidth:'550px'}} onClick={function(e) { e.stopPropagation() }}>
            <div style={s.modalHeader}>
              <div style={s.modalTitle}>REQUEST POOL INSTALL</div>
              <button onClick={function() { setShowRequest(false) }} style={s.modalClose}>X</button>
            </div>
            <div style={{fontSize:'13px', color:'#4a8c6a', marginBottom:'16px', fontWeight:600}}>
              {fmtDate(selectedDate)} {String.fromCodePoint(0x2022)} {getMaxForDate(selectedDate) - (jobCounts[selectedDate] || 0)} slots available
            </div>

            {/* === WHO IS THIS FOR? === */}
            <div style={{marginBottom:'16px',padding:'14px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px'}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:700,letterSpacing:'2px',color:'#b48c3c',marginBottom:'10px'}}>WHO IS THIS FOR?</div>
              {reqCustomerId ? (
                (function() {
                  var c = storeCustomers.find(function(x) { return x.id === reqCustomerId })
                  return (
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',padding:'8px 12px',backgroundColor:'rgba(180,140,60,0.08)',border:'1px solid rgba(180,140,60,0.3)',borderRadius:'3px'}}>
                      <div style={{fontSize:'13px'}}>
                        <div style={{color:'#ffffff',fontWeight:600}}>{c ? c.client_name : requestForm.client_name || 'Selected customer'}</div>
                        {c && <div style={{color:'rgba(255,255,255,0.4)',fontSize:'11px',marginTop:'2px'}}>{[c.client_phone,c.client_email].filter(Boolean).join(' \u00B7 ')}</div>}
                      </div>
                      <button type="button" onClick={clearPickedCustomer} style={{padding:'4px 10px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'3px',color:'rgba(255,255,255,0.6)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1px',cursor:'pointer'}}>CHANGE</button>
                    </div>
                  )
                })()
              ) : reqIsNewCustomer ? (
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',padding:'8px 12px',backgroundColor:'rgba(74,140,106,0.08)',border:'1px solid rgba(74,140,106,0.3)',borderRadius:'3px'}}>
                  <div style={{fontSize:'13px',color:'#4a8c6a',fontWeight:600}}>New customer \u2014 fill in details below</div>
                  <button type="button" onClick={clearPickedCustomer} style={{padding:'4px 10px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'3px',color:'rgba(255,255,255,0.6)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1px',cursor:'pointer'}}>CHANGE</button>
                </div>
              ) : (
                <div>
                  <input value={customerPickerSearch} onChange={function(e) { setCustomerPickerSearch(e.target.value) }} placeholder="Search existing customers..." style={{...s.formInput, marginBottom:'8px'}} />
                  {(function() {
                    var q = customerPickerSearch.toLowerCase().trim()
                    var matches = (storeCustomers || []).filter(function(c) {
                      if (!q) return true
                      return [c.client_name,c.client_email,c.client_phone,c.site_address]
                        .some(function(f) { return f && String(f).toLowerCase().includes(q) })
                    }).slice(0, 6)
                    if (matches.length === 0 && storeCustomers.length === 0) {
                      return <div style={{fontSize:'12px',color:'rgba(255,255,255,0.35)',padding:'8px 0'}}>No customers yet \u2014 create a new one below.</div>
                    }
                    if (matches.length === 0) {
                      return <div style={{fontSize:'12px',color:'rgba(255,255,255,0.35)',padding:'8px 0'}}>No matches. Try another search or create a new customer.</div>
                    }
                    return (
                      <div style={{display:'flex',flexDirection:'column',gap:'3px',maxHeight:'200px',overflowY:'auto',marginBottom:'8px'}}>
                        {matches.map(function(c) {
                          return (
                            <button key={c.id} type="button" onClick={function() { pickCustomer(c) }} style={{textAlign:'left',padding:'8px 12px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'3px',color:'#ffffff',cursor:'pointer',fontFamily:"'Barlow',sans-serif"}}>
                              <div style={{fontSize:'13px',fontWeight:600}}>{c.client_name}</div>
                              <div style={{fontSize:'11px',color:'rgba(255,255,255,0.4)',marginTop:'2px'}}>{[c.client_phone,c.client_email,c.site_address].filter(Boolean).join(' \u00B7 ')}</div>
                            </button>
                          )
                        })}
                      </div>
                    )
                  })()}
                  <button type="button" onClick={function() { setReqIsNewCustomer(true); setReqCustomerId(null) }} style={{padding:'8px 14px',backgroundColor:'rgba(74,140,106,0.15)',border:'1px solid rgba(74,140,106,0.3)',borderRadius:'3px',color:'#4a8c6a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',fontWeight:700,letterSpacing:'1.5px',cursor:'pointer'}}>+ CREATE NEW CUSTOMER</button>
                </div>
              )}
            </div>

            {/* Show quote summary if from calculator */}
            {requestForm.notes && parseFloat(requestForm.contract_value) > 0 && (
              <div style={{padding:'12px 16px',backgroundColor:'rgba(180,140,60,0.08)',border:'1px solid rgba(180,140,60,0.2)',borderRadius:'4px',marginBottom:'16px'}}>
                <div style={{fontSize:'10px',fontFamily:"'Barlow Condensed',sans-serif",fontWeight:600,letterSpacing:'1.5px',color:'#b48c3c',marginBottom:'6px'}}>QUOTE DETAILS</div>
                <div style={{fontSize:'12px',color:'rgba(255,255,255,0.5)',whiteSpace:'pre-wrap'}}>{requestForm.notes}</div>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,color:'#b48c3c',marginTop:'6px'}}>{fmtMoney(requestForm.contract_value)}</div>
              </div>
            )}

            <div style={s.formGrid}>
              <div style={s.field}><label style={s.formLabel}>CUSTOMER NAME *</label><input value={requestForm.client_name || ''} onChange={function(e) { set('client_name', e.target.value) }} style={s.formInput} placeholder="First Last" /></div>
              <div style={s.field}><label style={s.formLabel}>CUSTOMER PHONE</label><input value={requestForm.client_phone || ''} onChange={function(e) { set('client_phone', e.target.value) }} style={s.formInput} placeholder="(810) 555-1234" /></div>
              <div style={s.field}><label style={s.formLabel}>CUSTOMER EMAIL</label><input value={requestForm.client_email || ''} onChange={function(e) { set('client_email', e.target.value) }} style={s.formInput} placeholder="customer@email.com" /></div>
              <div style={s.field}><label style={s.formLabel}>SITE ADDRESS</label><input value={requestForm.site_address || ''} onChange={function(e) { set('site_address', e.target.value) }} style={s.formInput} placeholder="123 Main St, City, MI" /></div>
              <div style={s.field}>
                <label style={s.formLabel}>JOB TYPE</label>
                <select value={requestForm.job_type || 'above_ground'} onChange={function(e) { set('job_type', e.target.value) }} style={s.formInput}>
                  {JOB_TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option> })}
                </select>
              </div>
              <div style={s.field}>
                <label style={s.formLabel}>POOL SHAPE</label>
                <select value={requestForm.pool_shape || 'round'} onChange={function(e) { set('pool_shape', e.target.value) }} style={s.formInput}>
                  <option value="round">Round</option>
                  <option value="oval">Oval</option>
                </select>
              </div>
              <div style={s.field}><label style={s.formLabel}>POOL SIZE</label><input value={requestForm.pool_size || ''} onChange={function(e) { set('pool_size', e.target.value) }} style={s.formInput} placeholder="e.g. 24ft, 15x30" /></div>
              <div style={s.field}><label style={s.formLabel}>CONTRACT VALUE</label><input type="number" value={requestForm.contract_value || ''} onChange={function(e) { set('contract_value', e.target.value) }} style={s.formInput} placeholder="0.00" step="0.01" /></div>
              <div style={{...s.field, gridColumn:'1 / -1'}}><label style={s.formLabel}>NOTES</label><textarea value={requestForm.notes || ''} onChange={function(e) { set('notes', e.target.value) }} style={{...s.formInput, minHeight:'60px', resize:'vertical'}} placeholder="Special instructions, access notes, etc." /></div>
              <div style={s.field}><label style={s.formLabel}>PREFERRED SERVICE DATE</label><input type="date" value={requestForm.preferred_date || ''} onChange={function(e) { set('preferred_date', e.target.value) }} style={s.formInput} /></div>
            </div>

            <button onClick={submitRequest} disabled={saving} style={{...s.submitBtn, opacity: saving ? 0.6 : 1, display:'flex', alignItems:'center', justifyContent:'center', gap:'8px'}}>
              {saving && <span style={{display:'inline-block',width:'16px',height:'16px',border:'2px solid rgba(10,15,26,0.3)',borderTopColor:'#0a0f1a',borderRadius:'50%',animation:'spin 0.6s linear infinite'}} />}
              {saving ? 'SUBMITTING...' : 'SUBMIT REQUEST'}
            </button>
            {saving && <style>{'@keyframes spin { to { transform: rotate(360deg) } }'}</style>}
          </div>
        </div>
      )}
    </div>
  )
}

var s = {
  container:{fontFamily:"'Barlow',sans-serif",maxWidth:'100%'},
  loading:{padding:'60px',textAlign:'center',color:'rgba(255,255,255,0.3)',fontSize:'15px'},
  header:{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'28px',flexWrap:'wrap',gap:'16px'},
  title:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'28px',fontWeight:700,letterSpacing:'3px',color:'#ffffff'},
  subtitle:{fontSize:'15px',color:'rgba(255,255,255,0.4)',marginTop:'4px'},
  message:{padding:'12px 16px',backgroundColor:'rgba(74,140,106,0.15)',border:'1px solid rgba(74,140,106,0.3)',borderRadius:'4px',color:'#4a8c6a',fontSize:'13px',marginBottom:'20px'},
  tabs:{display:'flex',gap:'4px',marginBottom:'24px'},
  tab:{padding:'10px 20px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.4)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
  tabActive:{backgroundColor:'rgba(180,140,60,0.15)',borderColor:'rgba(180,140,60,0.4)',color:'#b48c3c'},
  sectionTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'14px',fontWeight:600,letterSpacing:'2px',color:'#b48c3c',marginBottom:'12px'},

  // Calculator styles
  poolTypeRow:{display:'flex',gap:'4px',marginBottom:'20px',flexWrap:'wrap'},
  poolTypeBtn:{padding:'10px 16px',backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.4)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
  poolTypeBtnActive:{backgroundColor:'rgba(180,140,60,0.15)',borderColor:'rgba(180,140,60,0.4)',color:'#b48c3c'},
  sizeGrid:{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(130px, 1fr))',gap:'8px'},
  sizeBtn:{padding:'16px 12px',backgroundColor:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'4px',cursor:'pointer',textAlign:'center'},
  sizeBtnActive:{backgroundColor:'rgba(180,140,60,0.15)',borderColor:'rgba(180,140,60,0.4)'},
  sizeLabel:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'14px',fontWeight:600,color:'#ffffff',letterSpacing:'1px'},
  sizePrice:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,color:'#b48c3c',marginTop:'6px'},
  extrasGrid:{display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))',gap:'6px'},
  extraBtn:{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 12px',backgroundColor:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'3px',cursor:'pointer',fontSize:'13px',color:'rgba(255,255,255,0.6)'},
  extraBtnActive:{backgroundColor:'rgba(74,140,106,0.15)',borderColor:'rgba(74,140,106,0.4)',color:'#4a8c6a'},
  extraPrice:{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:600,color:'#b48c3c'},
  addLineBtn:{padding:'6px 14px',backgroundColor:'rgba(180,140,60,0.15)',border:'1px solid rgba(180,140,60,0.3)',borderRadius:'3px',color:'#b48c3c',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1.5px',cursor:'pointer'},
  delSmBtn:{padding:'4px 8px',backgroundColor:'rgba(224,92,92,0.1)',border:'1px solid rgba(224,92,92,0.2)',borderRadius:'3px',color:'#e05c5c',fontSize:'12px',cursor:'pointer'},
  totalBar:{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'24px',paddingTop:'20px',borderTop:'1px solid rgba(255,255,255,0.08)',flexWrap:'wrap',gap:'16px'},
  totalBreakdown:{color:'rgba(255,255,255,0.4)',fontSize:'12px',marginRight:'16px'},
  grandTotal:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'28px',fontWeight:700,color:'#b48c3c'},
  scheduleBtn:{padding:'12px 24px',backgroundColor:'#b48c3c',border:'none',borderRadius:'4px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:700,letterSpacing:'2px',cursor:'pointer'},
  pendingQuote:{padding:'16px 20px',backgroundColor:'rgba(74,140,106,0.08)',border:'1px solid rgba(74,140,106,0.2)',borderRadius:'4px',marginBottom:'20px'},

  // Calendar styles
  calHeader:{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'},
  calMonth:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'20px',fontWeight:700,letterSpacing:'3px',color:'#ffffff'},
  calNav:{background:'none',border:'1px solid rgba(255,255,255,0.15)',borderRadius:'3px',padding:'8px 14px',color:'rgba(255,255,255,0.5)',fontSize:'16px',cursor:'pointer'},
  legend:{display:'flex',gap:'16px',justifyContent:'center',marginBottom:'16px'},
  legendItem:{display:'flex',alignItems:'center',gap:'6px',fontSize:'11px',color:'rgba(255,255,255,0.4)'},
  legendDot:{width:'8px',height:'8px',borderRadius:'50%',display:'inline-block'},
  calGrid:{display:'grid',gridTemplateColumns:'repeat(7, 1fr)',gap:'4px'},
  calDayHeader:{textAlign:'center',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1.5px',color:'rgba(255,255,255,0.25)',padding:'8px 0'},
  calDay:{padding:'10px 6px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',textAlign:'center',minHeight:'55px'},
  calDayNum:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'16px',fontWeight:600,color:'#ffffff'},

  // Jobs styles
  empty:{padding:'40px',textAlign:'center',color:'rgba(255,255,255,0.2)',fontSize:'14px'},
  jobCard:{display:'flex',alignItems:'center',gap:'16px',padding:'16px 20px',backgroundColor:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.06)',borderRadius:'4px',marginBottom:'4px'},
  jobTitle:{fontSize:'15px',fontWeight:600,color:'#ffffff'},
  jobMeta:{fontSize:'12px',color:'rgba(255,255,255,0.35)',marginTop:'3px'},
  jobAmount:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,color:'#ffffff'},
  statusBadge:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'1.5px',marginTop:'4px'},

  // Modal styles
  modalOverlay:{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.7)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:'24px'},
  modal:{backgroundColor:'#0f1420',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',padding:'32px',width:'100%',maxHeight:'85vh',overflow:'auto'},
  modalHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'20px'},
  modalTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'18px',fontWeight:700,letterSpacing:'2px',color:'#ffffff'},
  modalClose:{background:'none',border:'none',color:'rgba(255,255,255,0.4)',fontSize:'18px',cursor:'pointer'},
  formGrid:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px',marginBottom:'20px'},
  field:{},
  formLabel:{display:'block',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',fontWeight:600,letterSpacing:'2px',color:'rgba(255,255,255,0.3)',marginBottom:'6px'},
  formInput:{width:'100%',padding:'10px 12px',backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'#ffffff',fontSize:'14px',fontFamily:"'Barlow',sans-serif",outline:'none',boxSizing:'border-box'},
  submitBtn:{width:'100%',padding:'14px',backgroundColor:'#b48c3c',border:'none',borderRadius:'4px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'14px',fontWeight:700,letterSpacing:'2px',cursor:'pointer'},
}
